import { Divider, Grid, Link, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { green } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import React from 'react';
import { useHistory, useLocation } from 'react-router';

import { DataAPIContext } from '../api';
import { GET_ONE } from '../api/dataProvider';
import FixedLinearProgress from '../components/FixedLinearProgress';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { ReactComponent as GoToChatIcon } from '../icons/go_to_chat_link_2.svg';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import SentimentAnalysisListToolbar, { DEFAULT_DATE_PRESET, datePresetAfter } from './SentimentAnalysisListToolbar';
import { SentimentAnalysisModal } from './SentimentAnalysisDetails';
import { a11yProps } from '../components/TabPanel';
import ReviewList from './ReviewList';
import AgentMetricsList from './AgentMetricsList';
import NotificationMetricsList from './NotificationMetricsList';
import { AnalyticsHeaderSlotContext } from './AnalyticsHeaderSlot';

const NO_SORT = [];

const useStyles = makeStyles((theme) => ({
    root: {
        paddingBottom: '1rem !important',
    },
    rootWithMinWidth: {
        paddingBottom: '1rem !important',
        minWidth: '1400px',
    },
    recordListWrapper: {

        [theme.breakpoints.down('sm')]: {
            margin: '0 1rem !important',
        },
    },
    recordList: {
        border: 'none !important',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        height: `calc(${theme.cp.contentHeight} - 11.5rem) !important`,
    },
    listItem: {},
    dm_enabled: {
        color: green[800],
        fontWeight: 'bold',
    },
    dm_disabled: {
        textDecoration: 'line-through',
    },
    mainContainer: {
        margin: '1rem !important', border: 'solid 1px #D8D8D8 !important', borderRadius: '12px !important'
        ,
        [theme.breakpoints.down('sm')]: {
            margin: '0rem !important', border: 'none !important', borderRadius: '12px !important'
        },
    },
    mainContainerWithMinWidth: {
        margin: '1rem !important', border: 'solid 1px #D8D8D8 !important', borderRadius: '12px !important'
        ,
        [theme.breakpoints.down('sm')]: {
            margin: '0rem !important', border: 'none !important', borderRadius: '12px !important'
        },
        minWidth: '1400px',
    }
}));

const NOTIFICATION_METRICS_MIN_WIDTH_TABS = new Set([
    'notification_metrics',
    'notification_metrics_pg',
    'notification_metrics_ch',
]);

const HELP_ONLY_QA_TAB_TOOLTIP =
    'These QA tabs are only visible when signed in as help@curatedplanet.com.';

const qaTabSx = {
    fontSize: '0.6875rem',
    lineHeight: 1.25,
    color: 'text.secondary',
    opacity: 0.38,
    '&.Mui-selected': {
        color: 'text.secondary',
        opacity: 0.58,
    },
};

// Tabs in left-to-right display order, paired with the permission flag that
// makes each one visible. Used to pick a sensible default when no ?tab= is set.
const orderedTabPermFlags = (perms) => ([
    ['reviews', perms.hasReviewsListPerm],
    ['sentiment_analysis', perms.hasSentimentAnalysisListPerm],
    ['agent_metrics', perms.hasAgentMetricsPerm],
    ['notification_metrics', perms.hasNotificationMetricsPerm],
]);

const firstVisibleTab = (perms) => {
    const match = orderedTabPermFlags(perms).find(([, visible]) => visible);
    return match ? match[0] : 'reviews';
};

const getCurrentTab = (urlSearchParams, perms) => {
    const tab = new URLSearchParams(urlSearchParams).get('tab');
    if (tab) {
        return tab;
    }
    return firstVisibleTab(perms);
}

const SENTIMENT_FILTER_FIELDS = ['after', 'before', 'tour_id', 'tour_tags'];

// Filters and search from the URL, read once before the first render: setting them from a mount effect made
// the grid load twice. The URL is shared with the other Analytics tabs (Ratings reads the same `?0=` params),
// so it is read only on this tab. Without filters in it, the list opens on DEFAULT_DATE_PRESET.
export const initialSentimentState = (urlSearchParams, currentTab) => {
    const params = new URLSearchParams(currentTab === 'sentiment_analysis' ? urlSearchParams : '');
    let query = '';
    let filters = [];
    for (let [key, value] of params.entries()) {
        if (key === 'query') {
            query = value;
            continue;
        }
        else if (key === 'tab') {
            continue;
        }
        filters.push(JSON.parse(value));
    }
    if (filters.length === 0) {
        filters.push({ field: 'after', value: datePresetAfter(DEFAULT_DATE_PRESET), op: '>' });
    }
    // ensure all fields exist in the filters
    SENTIMENT_FILTER_FIELDS.forEach(field => {
        if (!filters.some(filter => filter.field === field)) {
            filters.push({ field, value: null });
        }
    });
    return { filters, query };
};

const SentimentAnalysisList = (props) => {
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'sentiment_analysis_list';
    const classes = useStyles();
    const [selectedIds] = React.useState([]);
    const [lastUpdate, setLastUpdate] = React.useState();
    const [syncActive, setSyncActive] = React.useState(false);
    const isSmallScreen = !useIsWidthUp('sm');
    const [headerSlotEl, setHeaderSlotEl] = React.useState(null);
    const { roleMap } = React.useContext(ContextStore);
    const [relatedLoading] = React.useState(false);
    const location = useLocation();
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectedAspects] = React.useState([]);
    const { hasPerm, isFranchise, licensees, currentLicensee, setCurrentLicensee } = React.useContext(ContextStore);

    // Franchise (CP-8027): the chat page is scoped to the selected licensee
    // (franchise_to). A sentiment row can belong to any licensee, so deep-linking
    // to its conversation while a different licensee — or "All licensees" — is
    // selected leaves the chat page unable to resolve it (Twilio
    // getConversationByUniqueName fails → error). Align the global licensee
    // context to the row's operator before navigating. A parent-direct row
    // (tour_operator not in the licensees list) maps to null = "All licensees",
    // which scopes chat to the parent where those conversations live. No-op for
    // standalone operators.
    const alignLicenseeForRow = (row) => {
        if (!isFranchise) return;
        const rowTo = row?.tour_operator;
        const targetLicensee = (licensees || []).some((l) => l.id === rowTo) ? rowTo : null;
        if (targetLicensee !== currentLicensee) {
            setCurrentLicensee(targetLicensee);
        }
    };

    const hasSentimentAnalysisListPerm = hasPerm('sentiment_analysis', 'list')
    const hasReviewsListPerm = hasPerm('reviews', 'list')
    const hasNotificationListPerm = hasPerm('notification', 'list')
    // notification.metrics is an additive perm: grants the Notification Metrics
    // tab without exposing the Notifications setup section (notification.list).
    const hasNotificationMetricsPerm = hasNotificationListPerm || hasPerm('notification', 'metrics')
    // CP-8224: Agent Metrics has its own additive scope (own < guides < all),
    // decoupled from chat.list. Any level shows the tab; the backend clamps rows.
    const hasAgentMetricsPerm = hasPerm('agent_metrics', 'all') || hasPerm('agent_metrics', 'guides') || hasPerm('agent_metrics', 'own')
    const tabPerms = { hasReviewsListPerm, hasSentimentAnalysisListPerm, hasAgentMetricsPerm, hasNotificationMetricsPerm };
    const [currentTab, setCurrentTab] = React.useState(getCurrentTab(location.search, tabPerms));
    const [initialState] = React.useState(() => initialSentimentState(location.search, currentTab));
    const [query, setQuery] = React.useState(initialState.query);

    const userIsAppSupport = (() => {
        try {
            return JSON.parse(window.localStorage.getItem('bootstrap'))?.user?.email
                === 'help@curatedplanet.com';
        } catch (_err) {
            return false;
        }
    })();

    // QA tabs are hidden in the UI for non–help users; deep links still need a redirect.
    React.useEffect(() => {
        const tab = new URLSearchParams(location.search).get('tab');
        if (
            (tab === 'notification_metrics_pg' || tab === 'notification_metrics_ch')
            && !userIsAppSupport
        ) {
            const sp = new URLSearchParams(location.search);
            sp.set('tab', 'notification_metrics');
            history.replace({ pathname: location.pathname, search: sp.toString() });
            setCurrentTab('notification_metrics');
        }
    }, [location.search, location.pathname, userIsAppSupport, history]);

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };
    const [recordListFilters, setRecordListFilters] = React.useState(initialState.filters);

    const handleTabChange = (ev, newValue) => {
        const searchParams = new URLSearchParams(location.search);

        const keys = [...searchParams.keys()]
        for (var key of keys) {
            console.log("Deleting: ", key, searchParams.get(key), searchParams.toString());
            searchParams.delete(key)
        };

        searchParams.set('tab', newValue);
        history.push(`/analytics?${searchParams.toString()}`);

        setCurrentTab(newValue);
      };



    // Update the URL whenever the filters or query change (on this tab only: the other tabs read the same params)
    React.useEffect(() => {
        if (currentTab !== 'sentiment_analysis') {
            return;
        }
        const params = new URLSearchParams();
        let filters = [...recordListFilters]
        filters.forEach((filter, index) => {
            if (filter.value) {
                params.append(index, JSON.stringify(filter));
            }
        });

        // Add the query to the URL parameters
        if (query) {
            params.append('query', query);
        }

        params.set('tab', currentTab);
        history.push({
            pathname: location.pathname,
            search: params.toString(),
        });
    }, [recordListFilters, query]);


    const ListItemRenderer = (row, idx, rows, gridApi) => {
        let chatChannelID = row.associated_twilio_conversation_unique_name?.split('::')[1];
        var comment = row.text;
        const noBorder = idx === rows.length - 1;

            var text = row.text;
            for (var i = 0; i < row.sentiment_analysis['terms'].length; i++) {
                if(row.sentiment_analysis['terms'][i]['sentiment'] == 'P' && row.sentiment_analysis['terms'][i]['term']){
                    text = text.replace(row.sentiment_analysis['terms'][i]['term'], '<span style="background-color:#DAF3E5">' + row.sentiment_analysis['terms'][i]['term'] + '</span>')
                }
                else if(row.sentiment_analysis['terms'][i]['sentiment'] == 'N' && row.sentiment_analysis['terms'][i]['term']){
                    text = text.replace(row.sentiment_analysis['terms'][i]['term'], '<span style="background-color:#FFDEDE">' + row.sentiment_analysis['terms'][i]['term'] + '</span>')
                }                    
            }


       return(
           <Grid container key={idx} style={{ borderBottom: noBorder ? 'none' : "1px solid #D0D5DD", paddingTop: "5px", paddingBottom: "8px" }}>
               <Grid item xs={12} container alignItems="center" justifyContent='space-between'>
                   <Grid item xs={'auto'} container alignItems="center" justifyContent='flex-start'>
                       <Grid item px={1}>
                           <span style={{ backgroundColor: row.sentiment_score < 4 ? "#FFDEDE" : row.sentiment_score < 7 ? "#FDF3E1" : "#DAF3E5", borderRadius: "14px", padding: "4px" }}>
                               {row.sentiment_score}

                           </span>
                           <span style={{ fontSize: "12px", marginLeft: "5px", overflow: 'scroll', display: 'inline-block' }} >{moment.utc(row.timestamp).format('MMM DD, YYYY')}</span>
                           <span style={{ fontSize: "12px", marginLeft: "5px", fontWeight: 500, overflow: 'scroll', display: 'inline-block', maxWidth: '190px', whiteSpace: 'nowrap' }} title={row.associated_twilio_conversation_channel_name_without_date_or_tags}>
                           {row.tour_id ? <Link href={"/trip/" + row.tour_id} style={{ textDecoration: 'none' }}> {row.associated_twilio_conversation_channel_name_without_date_or_tags}</Link>
                               :
                               row.associated_twilio_conversation_channel_name_without_date_or_tags}
                               {(row.tour_tags?.length > 0 || row.internal_tags?.length) && <br/>}
                               {(() => {
                                   if (row.tour_tags?.length > 0) {
                                       let tag_spans = row.tour_tags.flatMap((tag, idx) =>
                                           <span key={`m-tour-${idx}`} title={row.tour_tags.map(x => x.data?.friendly_name || x.name).join(", ")} style={{ fontSize: "11px", padding: "3px 4px 3px 4px", marginLeft: 4, color: "black", background: tag.category === 'departure' ? "#EAF4FF" : "#FDF3E1", borderRadius: 3, lineHeight: 1.15, display: "inline-block" }}>{tag.data?.friendly_name || tag.name}</span>
                                       )
                                       return tag_spans
                                   }
                               })()}
                               {(() => {
                                   if (row.internal_tags?.length > 0) {
                                       let tag_spans = row.internal_tags.flatMap((tag, idx) =>
                                           <span key={`m-int-${idx}`} title={row.internal_tags.map(x => x.data?.friendly_name || x.name).join(", ")} style={{ fontSize: "11px", padding: "3px 4px 3px 4px", marginLeft: 4, color: "black", background: "#f8d7da", borderRadius: 3, lineHeight: 1.15, display: "inline-block" }}>{tag.data?.friendly_name || tag.name}</span>
                                       )
                                       return tag_spans
                                   }
                               })()}
                           </span>
                       </Grid>

                   </Grid>
                    <Grid item pr={1} alignItems="center">
                        {row.associated_twilio_conversation_unique_name && row.associated_twilio_conversation_non_guest_participants_state !== 'deleted' && <GoToChatIcon style={{ cursor: 'pointer', marginBottom: '0.25rem' }} onClick={() => { alignLicenseeForRow(row); history.push('/chat/' + chatChannelID + '/conversations/' + row.associated_twilio_conversation_unique_name + '/interactivechat'); }} />}
                    </Grid>
                </Grid>
                <Grid item xs={12} container alignItems="center" justifyContent='space-between'>
                    <Grid item px={1}>
                        <span style={{ fontSize: "12px", marginTop: "5px", color: "blue", overflow: 'scroll', display: 'inline-block', maxWidth: '250px', whiteSpace: 'nowrap' }} color="primary" onClick={() => history.push(`/user/${row.sender.id}`)}>
                            {row.sender?.display_name}
                        </span>
                    </Grid>
                </Grid>
                <Grid item xs={12} px={1} py={0} gap={1} container >
                   {comment && <Grid item container xs={'auto'}>
                       <div style={{ whiteSpace: "pre-wrap", textOverflow: "ellipsis", maxWidth: "85vw", maxHeight: "100px", overflowY: "auto", overflowWrap: "break-word" }}>
                           <Typography
                               title={row.text}
                               style={{ fontSize: ".75rem", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical" }}
                               dangerouslySetInnerHTML={{
                                   __html:
                                       text
                               }}
                           />
                       </div>
                   </Grid>}
                </Grid>
            </Grid>)
    };

    const onExportList = async () => {
        try {
            setSyncActive(true);
            const params = {
                filter: {
                    filters: JSON.stringify(recordListFilters),
                },
                urlSuffix: "export/",
            };
            console.log('recordList', params);

            console.log('onExportList: requesting data', params);
            const res = await dataAPI(GET_ONE, resource, params);
            const body = await res.response.blob();
            console.log('onExportList: got response', body);
            const url = window.URL.createObjectURL(body);

            var filename = "";
            const disposition = res.headers['Content-Disposition'];
            if (disposition && disposition.indexOf('attachment') !== -1) {
                var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                var matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) { 
                    filename = matches[1].replace(/['"]/g, '');
                }
            }
            if (!filename || filename.length <= 0) {
                filename = "sentiment_analysis.csv";
            }
            console.log('onExportList: downloading as file', filename, url);

            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("onExportList: Error exporting list:", error);
        } finally {
            setSyncActive(false);
        }
    }

    const record_list_cols = [
        ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
        {
            field: 'timestamp',
            headerName: 'Date',
            headerAlign: 'center',
            sortable: true,
            width: 100,
            renderCell: (p) => <>{moment(p.row.timestamp).format('MMM DD, YYYY')}</>,
        },
        {
            field: 'sentiment_score',
            align: 'center',
            headerName: 'Sentiment',
            headerAlign: 'center',
            sortable: true,
            width: 100,
            renderCell: (p) => {
                return <span style={{ display: "flex", alignItems: "center" }}>
                    {
                    /*<img style={{ width: '50px', height: '50px' }} src={`/rating-emojis/rating-${Math.floor(p.row.sentiment_score / 2 + 1)}.svg`} />*/
            }
                    <span style={{ backgroundColor: p.row.sentiment_score < 4 ? "#FFDEDE" : p.row.sentiment_score < 7 ? "#FDF3E1" : "#DAF3E5", borderRadius: "17px", padding: "7px" }}>
                        {p.row.sentiment_score}
                    </span>
                </span>;
            },
        },
        {
            field: 'associated_twilio_conversation_channel_name_without_date_or_tags',
            headerName: 'Tour',
            sortable: true,
            flex: 0.4,
            renderCell: (p) => (
                <span title={p.row.associated_twilio_conversation_channel_name_without_date_or_tags}>
                    {p.row.tour_id ? <Link href={"/trip/" + p.row.tour_id} style={{ textDecoration: 'none' }}> {p.row.associated_twilio_conversation_channel_name_without_date_or_tags}</Link>
                        :
                        p.row.associated_twilio_conversation_channel_name_without_date_or_tags}
                    {(p.row.tour_tags?.length > 0 || p.row.internal_tags?.length > 0) && <br />}
                    {(() => {
                        if (p.row.tour_tags?.length > 0) {
                            let tag_spans = p.row.tour_tags.flatMap((tag, idx) => 
                                <span key={`tour-${idx}`} title={p.row.tour_tags.map(x => x.data?.friendly_name || x.name).join(", ")} style={{ fontSize: ".88rem", padding: "3px 4px 3px 4px", marginRight: 4, color: "black", background: tag.category === 'departure' ? "#EAF4FF" : "#FDF3E1", borderRadius: 3, lineHeight: 1.15, display: "inline-block" }}>{tag.data?.friendly_name || tag.name}</span>
                            )
                            return tag_spans
                        }
                    })()}
                    {(() => {
                        if (p.row.internal_tags?.length > 0) {
                            let tag_spans = p.row.internal_tags.flatMap((tag, idx) =>
                                <span key={`int-${idx}`} title={p.row.internal_tags.map(x => x.data?.friendly_name || x.name).join(", ")} style={{ fontSize: ".88rem", padding: "3px 4px 3px 4px", marginRight: 4, color: "black", background: "#f8d7da", borderRadius: 3, lineHeight: 1.15, display: "inline-block" }}>{tag.data?.friendly_name || tag.name}</span>
                            )
                            return tag_spans
                        }
                    })()}
                </span>
            ),
        },
        /*{
            field: 'tour-date',
            headerName: 'Trip Date',
            sortable: false,
            flex: 0.2,
            renderCell: (p) => (
                <Tooltip title={p.row.participant.tour.start_ts}>
                    <Typography fontWeight={500}>
                        {moment.utc(p.row.participant.start_ts).format('MMM DD, YYYY')}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'tour-end-date',
            headerName: 'Trip End Date',
            sortable: false,
            flex: 0.2,
            renderCell: (p) => (
                <Tooltip title={p.row.participant.tour.end_ts}>
                    <Typography fontWeight={500}>
                        {moment.utc(p.row.participant.end_ts).format('MMM DD, YYYY')}
                    </Typography>
                </Tooltip>
            ),
        },*/
        {
            field: 'sender',
            headerName: 'Name',
            sortable: false,
            flex: 0.5,
            renderCell: (p) => (
                <a style={{fontSize:".9rem"}} href={`/user/${p.row.sender?.id}`} target="_blank" rel="noopener noreferrer">
                {p.row.sender?.display_name}
                </a>
            ),
        },
        {
            field: 'text',
            headerName: 'Message',
            sortable: false,
            flex: 1,
            renderCell: (p) => {
                var text = p.row.text;
                for (var i = 0; i < p.row.sentiment_analysis['terms']?.length; i++) {
                    if(p.row.sentiment_analysis['terms'][i]['sentiment'] == 'P' && p.row.sentiment_analysis['terms'][i]['term']){
                        text = text.replace(p.row.sentiment_analysis['terms'][i]['term'], '<span style="background-color:#DAF3E5">' + p.row.sentiment_analysis['terms'][i]['term'] + '</span>')
                    }
                    else if(p.row.sentiment_analysis['terms'][i]['sentiment'] == 'N' && p.row.sentiment_analysis['terms'][i]['term']){
                        text = text.replace(p.row.sentiment_analysis['terms'][i]['term'], '<span style="background-color:#FFDEDE">' + p.row.sentiment_analysis['terms'][i]['term'] + '</span>')
                    }                    
                }
                return (
                    <div style={{whiteSpace: "pre-wrap", textOverflow: "ellipsis"}}>
                        <Typography
                              title={p.row.text}
                              style={{fontSize:".75rem", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical"}}
                              dangerouslySetInnerHTML={{
                                __html:
                                  text
                              }}
                              />
                    </div>
                );
            },
        },

        {
            field: 'buttons',
            headerName: 'Chat',
            sortable: false,
            headerAlign: 'left',
            //align: 'center',
            width: 50,
            renderCell: (p) => {
                if (p.row.associated_twilio_conversation_non_guest_participants_state === 'deleted' || !p.row.associated_twilio_conversation_unique_name) {
                    return <></>;
                }
                let chatChannelID = p.row.associated_twilio_conversation_unique_name?.split('::')[1];
                return <a style={{margin: 'auto'}} href={'/chat/' + chatChannelID + '/conversations/' + p.row.associated_twilio_conversation_unique_name + '/interactivechat'} target="_blank" rel="noopener noreferrer" onClick={() => alignLicenseeForRow(p.row)}><GoToChatIcon style={{ cursor: 'pointer', margin: 'auto' }} /></a>;
            },
        },
    ];
    if(isModalOpen){
        return <SentimentAnalysisModal open={isModalOpen} onClose={handleCloseModal} aspects={selectedAspects} />;
    }
    return (
        <AnalyticsHeaderSlotContext.Provider value={headerSlotEl}>
        <div className={classes.root}>
            <PageMeta title="Analytics" />
            <FixedLinearProgress visibility={syncActive} />
            <Grid item xs={12} pt={2} pl={2} pr={2} container justifyContent="space-between" alignItems="center" wrap="nowrap">
                <Typography fontSize={20} fontWeight={500} lineHeight="24px">
                    Analytics
                </Typography>
                <div ref={setHeaderSlotEl} />
            </Grid>
            <Grid item xs={12} className={(currentTab === 'agent_metrics' || NOTIFICATION_METRICS_MIN_WIDTH_TABS.has(currentTab)) ? classes.mainContainerWithMinWidth : classes.mainContainer} >
                    <Tabs className={classes.tabsMenu} value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto" >
                        { hasReviewsListPerm && <Tab label="Ratings" {...a11yProps('reviews')} /> }
                        { hasSentimentAnalysisListPerm && <Tab label="Sentiment Analysis" {...a11yProps('sentiment_analysis')} /> }
                        { hasAgentMetricsPerm && <Tab label="Agent Metrics" {...a11yProps('agent_metrics')} /> }
                        { hasNotificationMetricsPerm && <Tab label="Notification Metrics" {...a11yProps('notification_metrics')} /> }
                        { hasNotificationListPerm && userIsAppSupport && (
                            <Tooltip title={HELP_ONLY_QA_TAB_TOOLTIP} arrow placement="bottom">
                                <Tab
                                    label="Notification Metrics (PG QA)"
                                    sx={qaTabSx}
                                    {...a11yProps('notification_metrics_pg')}
                                />
                            </Tooltip>
                        )}
                        { hasNotificationListPerm && userIsAppSupport && (
                            <Tooltip title={HELP_ONLY_QA_TAB_TOOLTIP} arrow placement="bottom">
                                <Tab
                                    label="NOTIFICATION METRICS (CH+PG - LIVE VERSION)"
                                    sx={qaTabSx}
                                    {...a11yProps('notification_metrics_ch')}
                                />
                            </Tooltip>
                        )}
                    </Tabs>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                {currentTab === 'sentiment_analysis' && hasSentimentAnalysisListPerm ? <span>
                <Grid item xs={12} p={isSmallScreen && 2} rb={0}>
                    <SentimentAnalysisListToolbar
                        resource={resource} SentimentAnalysi
                        query={query} 
                        setRecordListFilters={setRecordListFilters} 
                        setQuery={setQuery} 
                        selectedIds={selectedIds} 
                        isSmallScreen={isSmallScreen} 
                        roleMap={roleMap} 
                        recordListFilters={recordListFilters} 
                        setLastUpdate={setLastUpdate} 
                        setSyncActive={setSyncActive}
                        onExportList={onExportList}
                    />
                </Grid>
                <Grid item xs={12} className={classes.recordListWrapper}>
                    <RecordList
                        className={classes.recordList}
                        resource={resource}
                        disableSelectionOnClick={true}
                        apiFilters={recordListFilters}
                        query={query}
                        // No starting sort. There is no `name` column: the grid dropped a `name` sort on
                        // mount and the list fetched again, three list requests per page load instead of
                        // one (2026-10-29). The server orders newest first either way.
                        sortModel={NO_SORT}
                        deps={lastUpdate}
                        disableColumnFilter={true}
                        isSmallScreen={isSmallScreen}
                        listItemRenderer={ListItemRenderer}
                        checkboxSelection={false}
                        disablePageUrlData={true}
                        rowHeight={125}
                        relatedLoading={relatedLoading}
                        columns={record_list_cols}
                    />
                </Grid>
                </span> : currentTab === 'reviews' && hasReviewsListPerm ? <ReviewList/> : currentTab === 'agent_metrics' && hasAgentMetricsPerm ? <AgentMetricsList/> : currentTab === 'notification_metrics' && hasNotificationMetricsPerm ? (
                        <NotificationMetricsList
                            forceSource="ch"
                            showDebugInfo={false}
                            analyticsTabSlug="notification_metrics"
                        />
                    ) : currentTab === 'notification_metrics_pg' && hasNotificationListPerm && userIsAppSupport ? (
                        <NotificationMetricsList
                            forceSource="pg"
                            showDebugInfo
                            analyticsTabSlug="notification_metrics_pg"
                        />
                    ) : currentTab === 'notification_metrics_ch' && hasNotificationListPerm && userIsAppSupport ? (
                        <NotificationMetricsList
                            forceSource="ch"
                            showDebugInfo
                            analyticsTabSlug="notification_metrics_ch"
                        />
                    ) : <></>}
            </Grid>
        </div>
        </AnalyticsHeaderSlotContext.Provider>
    );
};

export default SentimentAnalysisList;
